import { useEffect, useState } from 'react'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import { leadsApi, type Lead, type LeadJourney, type LeadCreateResult, callOutcomesApi, type CallOutcome, type LeadCallAttempt, type CreateCallAttemptRequest, RECONTACT_REASON_MAX } from '../api/leads'
import { customersApi, type CustomerCreateRequest } from '../api/customers'
import { DuplicateConflictError, type DuplicateCandidate } from '../api/client'
import { fetchMe } from '../api/auth'
import { useFeatureFlag } from '../lib/featureFlags'
import { RevealSensitiveButton } from '../components/RevealSensitiveButton'
import { isMasked } from '../api/sensitive'
import { tasksApi, type TaskCreateRequest } from '../api/operations'
import { LeadCallCenterPanel } from '../components/LeadCallCenterPanel'
import { leadStatusesApi, type LeadStatusDefinition } from '../api/leadStatuses'
import {
  Button, ButtonLink, Callout, Card, CardContent, CardHeader, Chip, Dialog, FormGrid, Icon, KeyValueList, LoadingState, OverflowMenu, Page, PageHeader, PaneLayout,
  Select, StatusPill, TextField, useSnackbar, type PillTone,
  usePrompt,
} from '../components/ui'

/** Same map as `CustomerFormPage.tsx` (TASK-0046/TASK-0031) — API route segment for each
 * `DuplicateCandidate.entityType` value the backend's duplicate-detection service can return (see
 * `DuplicateCheckModels.cs` / `CustomerDuplicateDetectionService.cs`). Duplicated here rather than
 * imported because `CustomerFormPage.tsx` doesn't export it and this task's `allowed_paths` don't
 * include a shared-component file to hoist it into. */
const ENTITY_TYPE_API_SEGMENT: Record<string, string> = {
  Person: 'people',
  Company: 'companies',
  Contact: 'contacts',
  Customer: 'customers',
}

function candidateApiUrl(c: DuplicateCandidate): string {
  const segment = ENTITY_TYPE_API_SEGMENT[c.entityType] ?? c.entityType.toLowerCase()
  return `/api/${segment}/${c.id}`
}

// Extended interface for the detail view — the API returns additional fields
// beyond the list-view subset defined in the shared Lead interface.
interface LeadDetail extends Lead {
  priority?: string
  estimatedValue?: number
  notes?: string
  pipelineId?: number
  stageId?: number
  convertedAt?: string
  convertedCustomerId?: number
  // F-076: il cliente (crm.contacts) a cui il lead è collegato, se c'è.
  contactId?: number | null
  // TASK-0034: the statuses this lead could transition to right now, per the backend's
  // LeadStatusTransitions applied graph (Lead.AvailableStatusTransitions) — the single source of
  // truth for which status buttons below should be clickable, instead of a second copy of the
  // transition graph duplicated here that could drift out of sync with the one the backend
  // actually enforces. Empty (or absent, for a response from before this field existed) means "no
  // valid transition from here" — every button below is then disabled except the current status.
  availableStatusTransitions?: string[]
}

/** F-080 (REQ-087-001): spiegazione del punteggio salvata sul lead (`Lead.ScoreExplanationJson`). */
interface ScoreExplanation {
  total: number
  raw: number
  clamped: boolean
  truncated?: boolean
  rules: { id: number; name: string; points: number }[]
}

function parseScoreExplanation(raw: string | null | undefined): ScoreExplanation | null {
  if (!raw) return null
  try {
    const parsed = JSON.parse(raw) as ScoreExplanation
    return Array.isArray(parsed.rules) ? parsed : null
  } catch {
    return null
  }
}

/** F-080 (REQ-107-001): testo italiano dell'azione decisa dalle regole di proprietà. */
const OWNERSHIP_ACTION_LABELS: Record<string, string> = {
  MantieniProprietario: 'Il lead è rimasto al proprietario del cliente',
  AssegnaNuovo: "Il lead è stato assegnato con l'instradamento ordinario",
  TeamLeader: 'Il lead è andato al responsabile del proprietario',
}

const TYPE_LABELS: Record<string, string> = {
  hot: 'Hot',
  warm: 'Warm',
  cold: 'Cold',
  person: 'Persona',
  company: 'Azienda',
}
const TYPE_COLORS: Record<string, string> = {
  hot: 'danger',
  warm: 'warning',
  cold: 'info',
  person: 'secondary',
  company: 'info',
}

const STATUS_LABELS: Record<string, string> = {
  new: 'Nuovo',
  contacted: 'Contattato',
  qualified: 'Qualificato',
  converted: 'Convertito',
  lost: 'Perso',
}
const STATUS_COLORS: Record<string, string> = {
  new: 'primary',
  contacted: 'info',
  qualified: 'success',
  converted: 'dark',
  lost: 'danger',
}

const PRIORITY_LABELS: Record<string, string> = {
  low: 'Bassa',
  medium: 'Normale',
  high: 'Alta',
  urgent: 'Urgente',
}
const PRIORITY_COLORS: Record<string, string> = {
  low: 'secondary',
  medium: 'warning',
  high: 'danger',
  urgent: 'danger',
}

// TASK-0170 (REQ-088-001). Same idiom as PRIORITY_LABELS/PRIORITY_COLORS immediately above —
// `Lead.Temperature`'s 4 applied values (see `Lead.cs`), Italian labels from the requirement text
// (Cold/Warm/Hot/Very hot). Not to be confused with TYPE_LABELS/TYPE_COLORS above, which also
// happen to define `hot`/`warm`/`cold` keys but are keyed by `lead.leadType` (person|company),
// never actually reached by those three values today — a pre-existing, unrelated oddity in this
// file, left untouched (out of this task's scope).
const TEMPERATURE_LABELS: Record<string, string> = {
  cold: 'Freddo',
  warm: 'Tiepido',
  hot: 'Caldo',
  very_hot: 'Molto caldo',
}
const TEMPERATURE_COLORS: Record<string, string> = {
  cold: 'info',
  warm: 'warning',
  hot: 'danger',
  very_hot: 'dark',
}

// Nome del colore Bootstrap usato dai dati -> tono dello StatusPill.
const PILL_TONES: Record<string, PillTone> = {
  primary: 'primary',
  secondary: 'neutral',
  success: 'success',
  danger: 'error',
  warning: 'warning',
  info: 'info',
  dark: 'neutral',
}
function pillTone(color: string): PillTone {
  return PILL_TONES[color] ?? 'neutral'
}
// Ruolo di colore M3 per il pallino della cronologia (coppia X-container / on-X-container).
const TONE_TOKENS: Record<PillTone, string> = {
  neutral: 'secondary',
  primary: 'primary',
  success: 'success',
  warning: 'warning',
  error: 'error',
  info: 'info',
}

const STATUS_BUTTONS = [
  { key: 'new',       label: 'Nuovo' },
  { key: 'contacted', label: 'Contattato' },
  { key: 'qualified', label: 'Qualificato' },
  { key: 'lost',      label: 'Perso' },
]

/**
 * TASK-0150 (REQ-069-001): the 14 Lost-reason values, verbatim — must match
 * `LeadLostReasons`'s applied vocabulary in `Lead.cs` exactly (string-for-string, since the backend
 * validates `reason` against that same set when `status === 'lost'` and rejects anything else with
 * a 400). Not derived from an API call — like `STATUS_BUTTONS` above, this list is small, static,
 * and this task's `allowed_paths` has no shared-contract file to fetch it from at runtime.
 */
const LOST_REASONS = [
  'Prezzo',
  'Non interessato',
  'Già cliente',
  'Vincolato con la concorrenza',
  'Irraggiungibile',
  'Numero errato',
  'Fuori target',
  'Ripensamento',
  'Documentazione mancante',
  'Problema tecnico',
  'Copertura insufficiente',
  'Offerta della concorrenza',
  'Richiamare più avanti',
  'Altro',
]

/**
 * TASK-0182 (REQ-106-001): the 6 Contest-reason values, verbatim — must match
 * `LeadContestReasons`'s applied vocabulary in `Lead.cs` exactly (string-for-string, since the
 * backend validates `reason` against that same set and rejects anything else with a 400). Same
 * idiom as `LOST_REASONS` above: small, static, not derived from an API call.
 */
const CONTEST_REASONS = [
  'Numero falso',
  'Duplicato',
  'Fuori zona',
  'Cliente già acquisito',
  'Informazioni errate',
  'Fuori target',
]

/**
 * TASK-0047: the pre-filled "create Customer" form shown in the conversion modal.
 * `customerType` uses the domain vocabulary directly (`'person' | 'company'`) — unlike
 * `CustomerFormPage.tsx`'s form, there is no separate Italian vocabulary to map from here, since
 * `Lead.LeadType` (see `Lead.cs`) already only ever holds `'person' | 'company'`.
 */
interface ConvertFormState {
  customerType: 'person' | 'company'
  firstName: string
  lastName: string
  companyName: string
  email: string
  phone: string
}

const EMPTY_CONVERT_FORM: ConvertFormState = {
  customerType: 'person',
  firstName: '',
  lastName: '',
  companyName: '',
  email: '',
  phone: '',
}

/**
 * TASK-0050: "Aggiungi Task" modal state — modeled directly on `OperationsPage.tsx`'s
 * `TaskForm`/`defaultTaskForm` (same fields, same modal chrome below), the one difference being
 * that `handleCreateTask` here also sends `leadId: lead.id` so the created task is linked back to
 * this lead (`TaskCreateRequest.leadId`, already accepted by `TasksController.Create` — no backend
 * change needed).
 */
interface TaskForm {
  title: string
  description: string
  priority: string
  dueDate: string
}

const EMPTY_TASK_FORM: TaskForm = { title: '', description: '', priority: 'normal', dueDate: '' }

function fmtDate(s: string): string {
  return new Date(s).toLocaleDateString('it-IT', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  })
}

/** F-045: `min` for the recontact datetime-local input (local time, "now" rounded up to the minute). */
function localDateTimeMin(): string {
  const d = new Date(Date.now() + 60_000)
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
}

/**
 * REQ-061-002: same `it-IT` locale as `fmtDate` above, but with time-of-day — the journey timeline's
 * events (status changes, appointments, contract transitions) can share a calendar day, so the date
 * alone would not distinguish their order the way `occurredAt`/`startAt` genuinely can.
 */
function fmtDateTime(s: string): string {
  return new Date(s).toLocaleString('it-IT', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

/**
 * REQ-061-002: one entry in the merged, chronological "Percorso Lead" timeline below — the whole
 * point of this requirement ("reconstruction of entire lead path... without losing connection to
 * original source") is a single ordered view across the three sources `LeadJourney` returns
 * separately (status history / appointments / contract status history), not three disconnected
 * lists a reader has to interleave mentally themselves.
 */
interface JourneyEvent {
  at: string
  icon: string
  color: string
  label: string
  detail?: string | undefined
}

/**
 * Flattens a `LeadJourney` into one array of `JourneyEvent`s sorted oldest-first. Kept as a plain
 * function (not inlined in the render) so it stays trivially testable/readable on its own — the
 * merge itself is the only non-trivial logic this task's UI half adds.
 */
function buildJourneyTimeline(journey: LeadJourney): JourneyEvent[] {
  const events: JourneyEvent[] = []

  for (const h of journey.statusHistory) {
    events.push({
      at: h.occurredAt,
      icon: 'sync_alt',
      color: STATUS_COLORS[h.toStatus] ?? 'secondary',
      label: h.fromStatus
        ? `Stato: ${STATUS_LABELS[h.fromStatus] ?? h.fromStatus} → ${STATUS_LABELS[h.toStatus] ?? h.toStatus}`
        : `Stato iniziale: ${STATUS_LABELS[h.toStatus] ?? h.toStatus}`,
      detail: h.reason ?? h.note ?? undefined,
    })
  }

  // F-080 (REQ-109-001): tappe "assegnato" ed "escalation".
  if (journey.assignment?.assignedAt) {
    events.push({
      at: journey.assignment.assignedAt,
      icon: 'assignment_ind',
      color: 'primary',
      label: journey.assignment.assignedAgentId
        ? `Assegnato all'agente #${journey.assignment.assignedAgentId}`
        : 'Assegnato',
      // C-03: lead riservato al commerciale fino a questa data; senza attivita' torna libero.
      detail: journey.assignment.protectedUntil
        ? `Riservato fino al ${fmtDate(journey.assignment.protectedUntil)} (senza attività torna libero)`
        : undefined,
    })
  }
  if (journey.escalation && journey.escalation.level > 0 && journey.escalation.lastEscalatedAt) {
    events.push({
      at: journey.escalation.lastEscalatedAt,
      icon: 'notification_important',
      color: 'warning',
      label: journey.escalation.level >= 2
        ? "Escalation: avvisato il responsabile dell'agente"
        : "Escalation: avvisato l'agente",
      detail: "Nessuna attività dopo l'assegnazione",
    })
  }

  for (const a of journey.appointments) {
    events.push({
      at: a.startAt,
      icon: 'event',
      color: a.status === 'completed' ? 'success' : a.status === 'cancelled' ? 'danger' : 'info',
      label: `Appuntamento (${a.status})`,
      detail: a.outcome ? `Esito: ${a.outcome}${a.outcomeReason ? ` — ${a.outcomeReason}` : ''}` : undefined,
    })
  }

  if (journey.conversion.convertedAt) {
    events.push({
      at: journey.conversion.convertedAt,
      icon: 'person_add',
      color: 'dark',
      label: 'Convertito in Cliente',
      detail: journey.conversion.convertedContractId
        ? `Contratto #${journey.conversion.convertedContractId}`
        : undefined,
    })
  }

  for (const h of journey.contractStatusHistory ?? []) {
    events.push({
      at: h.occurredAt,
      icon: 'description',
      color: 'primary',
      label: h.fromStatus
        ? `Contratto: ${h.fromStatus} → ${h.toStatus}`
        : `Contratto creato: ${h.toStatus}`,
      detail: h.reason ?? h.note ?? undefined,
    })
  }

  return events.sort((a, b) => new Date(a.at).getTime() - new Date(b.at).getTime())
}

export function LeadDetailPage() {
  const { id: idParam } = useParams<{ id: string }>()
  const id = parseInt(idParam ?? '0', 10)
  const navigate = useNavigate()
  const snackbar = useSnackbar()
  const prompt = usePrompt()
  // F-080: esito delle regole di proprietà, passato dal form subito dopo la creazione (non è salvato sul lead).
  const location = useLocation()
  const ownershipDecision = (location.state as { ownershipDecision?: LeadCreateResult['ownershipDecision'] } | null)
    ?.ownershipDecision

  const [lead, setLead] = useState<LeadDetail | null>(null)
  // R5-01 (D-01): trattativa (preventivo in attesa di esito) aperta dal lead, dietro il flag «trattative.scheda».
  const dealsOn = useFeatureFlag('trattative.scheda')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // REQ-061-002: the "Percorso Lead" timeline data — loaded alongside the lead itself, but kept in
  // its own state/error pair rather than folded into `lead`/`error` above. A caller who can read the
  // lead (this page already loaded) but for whom `GET /journey` somehow fails should still see the
  // rest of the page; it should not blank the whole detail view over one extra section failing.
  const [journey, setJourney] = useState<LeadJourney | null>(null)
  const [journeyError, setJourneyError] = useState<string | null>(null)
  const [statusUpdating, setStatusUpdating] = useState(false)
  const [deleting, setDeleting] = useState(false)
  // F-097: il cestino dei lead è solo per l'Admin (UI affordance: il confine è il server).
  const [isAdmin, setIsAdmin] = useState(false)
  const [restoring, setRestoring] = useState(false)
  // F-102 (REQ-068-001): stati configurabili attivi (api/lead-statuses). Un errore qui non blocca la pagina.
  const [detailDefs, setDetailDefs] = useState<LeadStatusDefinition[]>([])
  useEffect(() => {
    leadStatusesApi.list(true).then(r => setDetailDefs(r.items)).catch(() => setDetailDefs([]))
  }, [])
  useEffect(() => {
    fetchMe().then(me => setIsAdmin(me !== null && ['Administrator', 'EqualAdmin'].includes(me.role))).catch(() => setIsAdmin(false))
  }, [])

  // TASK-0278 (F-025): call attempts and outcomes
  const [callAttempts, setCallAttempts] = useState<LeadCallAttempt[]>([])
  const [callAttemptsError, setCallAttemptsError] = useState<string | null>(null)
  const [outcomes, setOutcomes] = useState<CallOutcome[]>([])
  const [allOutcomes, setAllOutcomes] = useState<CallOutcome[]>([])
  const [outcomesError, setOutcomesError] = useState<string | null>(null)
  const [showCallModal, setShowCallModal] = useState(false)
  const [callForm, setCallForm] = useState<CreateCallAttemptRequest>({ outcomeId: 0 })
  const [savingCall, setSavingCall] = useState(false)
  const [callError, setCallError] = useState<string | null>(null)

  // TASK-0047: conversion modal state. `leadsApi.convert` needs a real CustomerId it doesn't have
  // on its own (see the API's doc comment) — this modal is the missing "create the Customer first"
  // step, pre-filled from the lead's own data and calling the real `POST /api/customers`
  // (TASK-0046) before calling `convert` with the id it returns.
  const [showConvertModal, setShowConvertModal] = useState(false)
  const [convertForm, setConvertForm] = useState<ConvertFormState>(EMPTY_CONVERT_FORM)
  const [converting, setConverting] = useState(false)
  const [convertError, setConvertError] = useState<string | null>(null)
  const [duplicateConflict, setDuplicateConflict] = useState<
    { message: string; candidates: DuplicateCandidate[] } | null
  >(null)

  // TASK-0050: "Aggiungi Task" modal state — same shape as OperationsPage.tsx's TasksSection.
  const [showTaskModal, setShowTaskModal] = useState(false)
  const [taskForm, setTaskForm] = useState<TaskForm>(EMPTY_TASK_FORM)
  const [savingTask, setSavingTask] = useState(false)

  // TASK-0150 (REQ-069-001): "Perso" reason modal state — same shape/chrome as the conversion
  // modal above (`showConvertModal`/`convertForm`/`converting`/`convertError`), the "requires extra
  // info before confirming" pattern this file already established. Only the 'lost' status button
  // opens this; every other status button still calls `handleStatusChange` directly, unchanged.
  const [showLostModal, setShowLostModal] = useState(false)
  const [lostReason, setLostReason] = useState('')
  const [lostSubmitting, setLostSubmitting] = useState(false)
  const [lostError, setLostError] = useState<string | null>(null)

  // TASK-0182 (REQ-106-001): "Contesta" reason modal state — exact same shape as the "Perso"
  // reason modal above (showLostModal/lostReason/lostSubmitting/lostError), renamed for contest.
  // Independent of the status buttons: contesting a lead is not a status transition, so this
  // modal is opened by its own "Contesta" button, not one of the STATUS_BUTTONS entries.
  const [showContestModal, setShowContestModal] = useState(false)
  const [contestReason, setContestReason] = useState('')
  const [contestSubmitting, setContestSubmitting] = useState(false)
  const [contestError, setContestError] = useState<string | null>(null)
  // F-120: decisione sulla contestazione (solo Admin): nota facoltativa e invio.
  const [decisionNote, setDecisionNote] = useState('')
  const [decisionSubmitting, setDecisionSubmitting] = useState(false)
  const [decisionError, setDecisionError] = useState<string | null>(null)

  async function load() {
    setLoading(true)
    setError(null)
    try {
      const data = await leadsApi.get(id)
      setLead(data as LeadDetail)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Errore nel caricamento')
    } finally {
      setLoading(false)
    }
  }

  /**
   * REQ-061-002: loads the "Percorso Lead" timeline. Deliberately a second request, not folded into
   * `load()`'s `try` above — see `journeyError`'s state comment for why a failure here must not
   * blank the rest of the page.
   */
  async function loadJourney() {
    setJourneyError(null)
    try {
      const data = await leadsApi.journey(id)
      setJourney(data)
    } catch (e) {
      setJourneyError(e instanceof Error ? e.message : 'Errore nel caricamento del percorso lead')
    }
  }

  /**
   * TASK-0278 (F-025): loads call attempts and outcomes separately, same pattern as loadJourney.
   * Failures here do not blank the rest of the page.
   * Loads two outcome lists: active-only for the modal buttons, and full for history translation.
   */
  async function loadCallData() {
    setCallAttemptsError(null)
    setOutcomesError(null)

    try {
      const attempts = await leadsApi.getCallAttempts(id)
      setCallAttempts(attempts.sort((a, b) => a.attemptNumber - b.attemptNumber))
    } catch (e) {
      setCallAttemptsError(e instanceof Error ? e.message : 'Errore nel caricamento dei tentativi')
    }

    try {
      const activeOutcomes = await callOutcomesApi.list(true)
      setOutcomes(activeOutcomes)
    } catch (e) {
      setOutcomesError(e instanceof Error ? e.message : 'Errore nel caricamento degli esiti')
    }

    try {
      const complete = await callOutcomesApi.list(false)
      setAllOutcomes(complete)
    } catch (e) {
      // Silent failure for allOutcomes — if active-only loads, the history can still work
    }
  }

  useEffect(() => {
    void load()
    void loadJourney()
    void loadCallData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id])

  async function handleDelete() {
    // F-097: non si cancella, si sposta nel cestino; si può ripristinare.
    const reason = await prompt({
      title: 'Sposta il lead nel cestino',
      message: 'Spostare questo lead nel cestino? Sparirà da elenchi, coda e report e si potrà ripristinare.',
      label: 'Motivo (facoltativo)',
      confirmLabel: 'Sposta nel cestino',
      destructive: true,
    })
    if (reason === null) return
    setDeleting(true)
    try {
      await leadsApi.delete(id, reason.trim() || undefined)
      navigate('/leads')
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Errore nello spostamento nel cestino')
      setDeleting(false)
    }
  }

  async function handleRestore() {
    setRestoring(true)
    try {
      await leadsApi.restore(id)
      await load()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Errore nel ripristino')
    } finally {
      setRestoring(false)
    }
  }

  /**
   * F-102: sceglie lo stato operativo / l'esito finale. Un valore dello stesso ciclo di vita del lead cambia solo il
   * dettaglio; un esito finale raggiungibile (es. Perso) porta anche il lead a quello stato, col motivo dello stato.
   */
  async function handleDetailChange(code: string) {
    if (!lead) return
    setStatusUpdating(true)
    setError(null)
    try {
      const def = detailDefs.find(d => d.code === code)
      if (code === '') await leadsApi.updateStatusDetail(id, null)
      else if (def && def.lifecycleStatus === lead.status) await leadsApi.updateStatusDetail(id, code)
      else await leadsApi.applyOutcome(id, code)
      await load()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Errore nel cambio dello stato')
    } finally {
      setStatusUpdating(false)
    }
  }

  async function handleStatusChange(status: string) {
    if (!lead || lead.status === status) return
    setStatusUpdating(true)
    try {
      await leadsApi.updateStatus(id, status)
      setLead(l => l ? { ...l, status } : l)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Errore nell\'aggiornamento stato')
    } finally {
      setStatusUpdating(false)
    }
  }

  /**
   * TASK-0150 (REQ-069-001): opens the reason-selection modal instead of transitioning directly —
   * called by the "Perso" status button in place of `handleStatusChange`. Every other status
   * button still calls `handleStatusChange('...')` straight away, unchanged.
   */
  function openLostModal() {
    if (!lead || lead.status === 'lost') return
    setLostReason('')
    setLostError(null)
    setShowLostModal(true)
  }

  function closeLostModal() {
    if (lostSubmitting) return
    setShowLostModal(false)
    setLostError(null)
  }

  /**
   * The actual transition, sent only once a reason has been picked — mirrors
   * `handleCreateAndConvert`'s shape (set submitting, clear error, call the API, update local state
   * or surface the error) rather than reusing `handleStatusChange` itself, since this needs to send
   * `reason` alongside `status` and control its own modal's open/submitting state.
   */
  async function handleConfirmLost() {
    if (!lead || !lostReason) return
    setLostSubmitting(true)
    setLostError(null)
    try {
      await leadsApi.updateStatus(id, 'lost', lostReason)
      setLead(l => l ? { ...l, status: 'lost' } : l)
      setShowLostModal(false)
    } catch (e) {
      setLostError(e instanceof Error ? e.message : 'Errore nell\'aggiornamento stato')
    } finally {
      setLostSubmitting(false)
    }
  }

  /**
   * TASK-0182 (REQ-106-001): opens the contest-reason modal — the "Contesta" quick-action button
   * calls this instead of contesting directly, same reasoning as `openLostModal` above. A lead
   * already contested need not be contested again (there is no un-contest action, so once
   * `isContested` is true the button below is not shown at all — see the JSX).
   */
  function openContestModal() {
    if (!lead) return
    setContestReason('')
    setContestError(null)
    setShowContestModal(true)
  }

  function closeContestModal() {
    if (contestSubmitting) return
    setShowContestModal(false)
    setContestError(null)
  }

  /**
   * The actual contest call, sent only once a reason has been picked — mirrors
   * `handleConfirmLost` exactly (set submitting, clear error, call the API, update local state or
   * surface the error), calling `leadsApi.markContested` instead of `leadsApi.updateStatus`.
   */
  async function handleDecideContest(accepted: boolean) {
    if (!lead) return
    setDecisionSubmitting(true)
    setDecisionError(null)
    try {
      await leadsApi.decideContest(id, accepted, decisionNote)
      setLead(l => l ? { ...l, contestStatus: accepted ? 'Accettata' : 'Respinta', contestDecisionNote: decisionNote.trim() || null } : l)
      setDecisionNote('')
    } catch (e) {
      setDecisionError(e instanceof Error ? e.message : 'Errore nella decisione')
    } finally {
      setDecisionSubmitting(false)
    }
  }

  async function handleConfirmContest() {
    if (!lead || !contestReason) return
    setContestSubmitting(true)
    setContestError(null)
    try {
      await leadsApi.markContested(id, contestReason)
      setLead(l => l ? { ...l, isContested: true, contestReason, contestStatus: 'Aperta' } : l)
      setShowContestModal(false)
    } catch (e) {
      setContestError(e instanceof Error ? e.message : 'Errore nella contestazione')
    } finally {
      setContestSubmitting(false)
    }
  }

  function openConvertModal() {
    if (!lead) return
    setConvertForm({
      customerType: lead.leadType === 'company' ? 'company' : 'person',
      firstName: lead.firstName ?? '',
      lastName: lead.lastName ?? '',
      companyName: lead.companyName ?? '',
      email: lead.email ?? '',
      // R2-MASK: un telefono mascherato non va copiato nel nuovo cliente.
      phone: isMasked(lead.phone) ? '' : lead.phone ?? '',
    })
    setConvertError(null)
    setDuplicateConflict(null)
    setShowConvertModal(true)
  }

  function closeConvertModal() {
    if (converting) return
    setShowConvertModal(false)
    setConvertError(null)
    setDuplicateConflict(null)
  }

  /**
   * TASK-0047: the real two-step conversion — create the Customer (`POST /api/customers`, real
   * since TASK-0046), then convert the lead with the id that call returns. Mirrors
   * `CustomerFormPage.tsx`'s `handleSave(forceCreate)` for the duplicate-409 handling (same
   * `DuplicateConflictError` panel, same "only clear the existing conflict when NOT retrying with
   * forceCreate" ordering, so the panel doesn't flicker away while the retry is in flight).
   *
   * Navigates using the Customer id returned by the *create* call, not
   * `res.convertedCustomerId` from `convert` — both are the same id (the create response is what
   * `leadsApi.convert` is called with), but the create response is available first and doesn't
   * depend on the convert call having succeeded, so it's the more reliable one to hold onto if the
   * second call fails (see the catch below, which surfaces the already-created customerId rather
   * than losing it).
   */
  async function handleCreateAndConvert(forceCreate = false): Promise<void> {
    if (!lead) return
    setConverting(true)
    if (!forceCreate) setDuplicateConflict(null)
    setConvertError(null)

    const body: CustomerCreateRequest = {
      customerType: convertForm.customerType,
      firstName: convertForm.firstName || undefined,
      lastName: convertForm.lastName || undefined,
      companyName: convertForm.companyName || undefined,
      email: convertForm.email || undefined,
      phone: convertForm.phone || undefined,
    }
    if (forceCreate) body.forceCreate = true

    let customerId: number
    try {
      const res = await customersApi.create(body)
      customerId = res.id
    } catch (e) {
      if (e instanceof DuplicateConflictError) {
        setDuplicateConflict({ message: e.message, candidates: e.candidates })
        setConverting(false)
        return
      }
      setConvertError(e instanceof Error ? e.message : 'Errore nella creazione del cliente')
      setConverting(false)
      return
    }

    try {
      await leadsApi.convert(id, customerId, null)
    } catch (e) {
      setConvertError(
        `Cliente creato (ID ${customerId}) ma la conversione del lead è fallita: ` +
        (e instanceof Error ? e.message : 'errore sconosciuto') +
        '. Riprova, oppure gestisci il cliente manualmente.',
      )
      setConverting(false)
      return
    }

    navigate('/customers/' + customerId)
  }

  function openTaskModal() {
    setTaskForm(EMPTY_TASK_FORM)
    setShowTaskModal(true)
  }

  function closeTaskModal() {
    if (savingTask) return
    setShowTaskModal(false)
  }

  /**
   * TASK-0050: mirrors `OperationsPage.tsx`'s `handleCreateTask` exactly, with one addition —
   * `leadId: lead.id` is always included so the created task is linked to this lead
   * (`TaskCreateRequest.leadId`, `TasksController.Create` already accepts it).
   */
  async function handleCreateTask() {
    if (!lead || !taskForm.title.trim()) return
    setSavingTask(true)
    try {
      const req: TaskCreateRequest = { title: taskForm.title.trim(), leadId: lead.id }
      if (taskForm.description.trim()) req.description = taskForm.description.trim()
      if (taskForm.priority) req.priority = taskForm.priority
      if (taskForm.dueDate) req.dueDate = new Date(taskForm.dueDate).toISOString()
      await tasksApi.create(req)
      setShowTaskModal(false)
      setTaskForm(EMPTY_TASK_FORM)
    } catch (e) {
      snackbar.show({ message: e instanceof Error ? e.message : 'Errore', tone: 'error' })
    } finally {
      setSavingTask(false)
    }
  }

  function openCallModal() {
    const form: CreateCallAttemptRequest = { outcomeId: 0 }
    if (lead?.phone && !isMasked(lead.phone)) form.phoneNumberCalled = lead.phone
    setCallForm(form)
    setCallError(null)
    setShowCallModal(true)
  }

  function closeCallModal() {
    if (savingCall) return
    setShowCallModal(false)
    setCallError(null)
  }

  async function handleCreateCallAttempt() {
    if (!lead || !callForm.outcomeId) return
    setSavingCall(true)
    setCallError(null)

    try {
      const req: CreateCallAttemptRequest = { outcomeId: callForm.outcomeId }
      if (callForm.phoneNumberCalled) req.phoneNumberCalled = callForm.phoneNumberCalled
      if (callForm.notes) req.notes = callForm.notes
      if (callForm.callbackScheduledAt) req.callbackScheduledAt = new Date(callForm.callbackScheduledAt).toISOString()
      // F-045: the reason only makes sense with a date (the server refuses it otherwise).
      const reason = (callForm.callbackReason ?? '').trim()
      if (callForm.callbackScheduledAt && reason) req.callbackReason = reason

      await leadsApi.createCallAttempt(id, req)
      setShowCallModal(false)
      await loadCallData()
    } catch (e) {
      if (e instanceof Error) {
        setCallError(e.message)
      } else {
        setCallError('Errore nel salvataggio')
      }
    } finally {
      setSavingCall(false)
    }
  }

  if (loading) {
    return <LoadingState />
  }

  if (error && !lead) {
    return <Callout tone="error">{error}</Callout>
  }

  if (!lead) {
    return <Callout tone="warning">Lead non trovato.</Callout>
  }

  const displayName = lead.companyName
    ? lead.companyName
    : `${lead.firstName} ${lead.lastName}`.trim()

  return (
    <Page>
      <PageHeader
        back={{ to: '/leads', label: 'Tutti i lead' }}
        title={displayName}
        icon="person_search"
        actions={
          <>
            {/* R2-MASK: telefono e codice fiscale arrivano mascherati; la visione motivata vale 15 minuti per questo lead. */}
            <RevealSensitiveButton entityType="leads" entityId={lead.id} values={[lead.phone, lead.fiscalCode]} onRevealed={() => void load()} />
            <ButtonLink to={`/leads/${id}/edit`} variant="outlined" icon="edit">
              Modifica
            </ButtonLink>
            {isAdmin && !lead.deletedAt && (
              <OverflowMenu
                aria-label="Altre azioni"
                items={[{ label: deleting ? 'Spostamento...' : 'Sposta nel cestino', icon: 'delete', onSelect: () => void handleDelete(), disabled: deleting }]}
              />
            )}
          </>
        }
      />

      {error && <Callout tone="error">{error}</Callout>}

      {lead.deletedAt && (
        <Callout
          tone="info"
          action={isAdmin ? (
            <Button variant="outlined" size="sm" onClick={() => void handleRestore()} disabled={restoring}>
              {restoring ? 'Ripristino...' : 'Ripristina'}
            </Button>
          ) : undefined}
        >
          <strong>Nel cestino</strong> dal {new Date(lead.deletedAt).toLocaleDateString('it-IT')}
          {lead.deleteReason ? ` — ${lead.deleteReason}` : ''}. Non compare in elenchi, coda e report.
        </Callout>
      )}

      {/* Header details */}
      <div>
        {/* F-076 (TASK-0330): link alla scheda cliente 360, solo se il lead è collegato a un contatto. */}
        {lead.contactId ? (
          <div className="mb-2">
            <ButtonLink to={`/contacts/${lead.contactId}`} variant="outlined" size="sm" icon="account_circle">
              Scheda cliente
            </ButtonLink>
          </div>
        ) : null}
        <div className="d-flex gap-2 flex-wrap">
          {lead.leadType && (
            <StatusPill tone={pillTone(TYPE_COLORS[lead.leadType] ?? 'secondary')}>
              {TYPE_LABELS[lead.leadType] ?? lead.leadType}
            </StatusPill>
          )}
          <StatusPill tone={pillTone(STATUS_COLORS[lead.status] ?? 'secondary')}>
            {STATUS_LABELS[lead.status] ?? lead.status}
          </StatusPill>
          {lead.statusDetailCode && (
            <StatusPill tone="neutral" title="Stato operativo / esito">
              {detailDefs.find(d => d.code === lead.statusDetailCode)?.label ?? lead.statusDetailCode}
            </StatusPill>
          )}
          {/* TASK-0182 (REQ-106-001): contested indicator, same badge idiom as the status/type
              badges immediately above — shown only once `isContested` is true, next to them as
              the most natural "at-a-glance" spot on this page. */}
          {lead.isContested && (
            <StatusPill tone="warning" title={lead.contestReason}>
              Contestato · {lead.contestStatus === 'Accettata' ? 'Accettata' : lead.contestStatus === 'Respinta' ? 'Respinta' : 'In attesa'}
              {lead.contestReason ? `: ${lead.contestReason}` : ''}
            </StatusPill>
          )}
          {/* F-080 (REQ-087-001): Lead Score 0-100 con la spiegazione nel tooltip; il dettaglio è nella scheda. */}
          {lead.score != null && (
            <StatusPill
              tone={lead.score >= 70 ? 'success' : lead.score >= 40 ? 'warning' : 'neutral'}
              title={(parseScoreExplanation(lead.scoreExplanationJson)?.rules ?? [])
                .map(r => `${r.name}: ${r.points > 0 ? '+' : ''}${r.points}`).join('\n') || 'Lead Score'}
            >
              Score {lead.score}
            </StatusPill>
          )}
        </div>
        {/* F-080 (REQ-107-001): come è stato assegnato il lead di un cliente che aveva già un proprietario. */}
        {ownershipDecision && (
          <Callout tone="info" className="mt-2">
            <strong>{OWNERSHIP_ACTION_LABELS[ownershipDecision.action] ?? ownershipDecision.action}.</strong>{' '}
            {ownershipDecision.reason}
          </Callout>
        )}
      </div>

      {/* Main content */}
      <PaneLayout
        variant="supporting"
        main={
          <>
            {/* Left: info card */}
            <Card variant="outlined">
              <CardHeader title="Informazioni lead" />
              <CardContent>
                <KeyValueList columns={2}
                  items={[
                    { label: 'Nome', value: <>{`${lead.firstName} ${lead.lastName}`.trim() || '—'}</> },
                    { label: 'Azienda', value: <>{lead.companyName || '—'}</> },
                    {
                      label: 'Email',
                      value: (
                        <>
                          {lead.email
                            ? <a href={`mailto:${lead.email}`}>{lead.email}</a>
                            : '—'}
                        </>
                      ),
                    },
                    {
                      label: 'Telefono',
                      value: (
                        <>
                          {lead.phone
                            ? (isMasked(lead.phone) ? lead.phone : <a href={`tel:${lead.phone}`}>{lead.phone}</a>)
                            : '—'}
                        </>
                      ),
                    },
                    /* F-110 (REQ-062-003): località del lead */
                    {
                      label: 'Località',
                      value: (
                        <>
                          {[lead.postalCode, lead.city, lead.province ? `(${lead.province})` : null]
                            .filter(Boolean).join(' ') || '—'}
                        </>
                      ),
                    },
                    {
                      label: 'Tipo Lead',
                      value: (
                        <>
                          {lead.leadType ? (
                            <StatusPill tone={pillTone(TYPE_COLORS[lead.leadType] ?? 'secondary')}>
                              {TYPE_LABELS[lead.leadType] ?? lead.leadType}
                            </StatusPill>
                          ) : '—'}
                        </>
                      ),
                    },
                    /* F-080 (REQ-087-001): punteggio e regole che lo compongono. */
                    {
                      label: 'Punteggio',
                      full: true,
                      value: (
                        <>
                          {lead.score != null ? (
                            <>
                              <span className="md-typescale-emphasized-body-medium">{lead.score}</span>
                              <span className="text-muted"> / 100</span>
                              {(() => {
                                const expl = parseScoreExplanation(lead.scoreExplanationJson)
                                if (!expl) return null
                                return (
                                  <ul className="list-unstyled md-typescale-body-small mb-0 mt-1">
                                    {expl.rules.map(r => (
                                      <li key={r.id}>
                                        <span className={r.points >= 0 ? 'text-success' : 'text-danger'}>
                                          {r.points > 0 ? '+' : ''}{r.points}
                                        </span>{' '}
                                        {r.name}
                                      </li>
                                    ))}
                                    {expl.clamped && (
                                      <li className="text-muted">Totale {expl.raw} portato a {expl.total} (limite 0-100)</li>
                                    )}
                                    {expl.truncated && <li className="text-muted">Elenco abbreviato</li>}
                                  </ul>
                                )
                              })()}
                            </>
                          ) : '—'}
                        </>
                      ),
                    },
                    {
                      label: 'Priorità',
                      value: (
                        <>
                          {lead.priority ? (
                            <StatusPill tone={pillTone(PRIORITY_COLORS[lead.priority] ?? 'secondary')}>
                              {PRIORITY_LABELS[lead.priority] ?? lead.priority}
                            </StatusPill>
                          ) : '—'}
                        </>
                      ),
                    },
                    /* TASK-0170 (REQ-088-001). `lead.temperature` (Lead entity field), NOT
                        `taskForm.priority` — that is a wholly unrelated field on the embedded
                        "Aggiungi Task" (CrmTask) form further down this page and is not touched here. */
                    {
                      label: 'Temperatura',
                      value: (
                        <>
                          {lead.temperature ? (
                            <StatusPill tone={pillTone(TEMPERATURE_COLORS[lead.temperature] ?? 'secondary')}>
                              {TEMPERATURE_LABELS[lead.temperature] ?? lead.temperature}
                            </StatusPill>
                          ) : '—'}
                        </>
                      ),
                    },
                    { label: 'Fonte', value: <>{lead.source || '—'}</> },
                    /* REQ-063-002 (TASK-0234): read-only surfacing of the original payload the lead
                        arrived with, "for later verification" per the requirement text. Collapsed
                        behind a <details> toggle since it can be arbitrarily long and is rarely needed
                        at a glance — same shape as every other attribution row on this page, but with
                        no edit affordance: the field is intentionally immutable after Create (backend
                        `Lead.RawSourceData`/`UpdateDetails`), so a read-only view is the only correct
                        surface. Rendered as plain text via React's normal child-text escaping — never
                        `dangerouslySetInnerHTML` — since this payload is externally supplied and its
                        shape/validity is not guaranteed. */
                    ...(lead.rawSourceData ? [
                      {
                        label: 'Dati Origine Grezzi',
                        full: true,
                        value: (
                          <>
                            <details>
                              <summary className="text-muted md-u-pointer">
                                Mostra payload originale
                              </summary>
                              <pre className="md-typescale-body-small mb-0 mt-2 md-u-pre-wrap md-u-break-word">
                                {lead.rawSourceData}
                              </pre>
                            </details>
                          </>
                        ),
                      },
                    ] : []),
                    ...(lead.estimatedValue !== undefined && lead.estimatedValue !== null ? [
                      { label: 'Valore Stimato', value: <>€ {lead.estimatedValue.toLocaleString('it-IT', { minimumFractionDigits: 2 })}</> },
                    ] : []),
                    { label: 'Creato il', value: <>{fmtDate(lead.createdAt)}</> },
                  ]}
                />
              </CardContent>
            </Card>

            {/* F-088 (TASK-0342): script di chiamata e questionario di qualificazione del lead. */}
            <LeadCallCenterPanel leadId={id} />

            {/* TASK-0278 (F-025): call attempts panel — shows the log of all attempts and provides
                buttons to register a new one. Positioned ahead of "Percorso Lead" so the most recent
                operational data appears first. */}
            <Card variant="outlined">
              <CardHeader
                title="Registra chiamata"
                actions={
                  <Button
                    variant="filled"
                    size="sm"
                    icon="add_call"
                    onClick={openCallModal}
                    disabled={!outcomes.length || outcomesError !== null}
                  >
                    Registra tentativo
                  </Button>
                }
              />
              <CardContent>
                {outcomesError && (
                  <Callout tone="warning" className="mb-3">{outcomesError}</Callout>
                )}
                {!outcomesError && outcomes.length === 0 && (
                  <p className="text-muted mb-0">Nessun esito disponibile per la registrazione.</p>
                )}
                {callAttemptsError && (
                  <Callout tone="warning" className="mb-3">{callAttemptsError}</Callout>
                )}
                {!callAttemptsError && callAttempts.length === 0 && (
                  <p className="text-muted mb-0">Nessun tentativo registrato.</p>
                )}
                {!callAttemptsError && callAttempts.length > 0 && (
                  <ul className="list-unstyled mb-0 d-flex flex-column gap-2">
                    {callAttempts.map(a => {
                      const outcome = allOutcomes.find(o => o.id === a.outcomeId)
                      return (
                        <li key={a.id} className="border-bottom pb-2">
                          <div className="d-flex justify-content-between align-items-start">
                            <div className="flex-grow-1">
                              <div className="md-typescale-emphasized-body-medium">
                                Tentativo #{a.attemptNumber}
                              </div>
                              {outcome && (
                                <div className="md-typescale-body-small">
                                  <StatusPill tone="primary">{outcome.label}</StatusPill>
                                </div>
                              )}
                              {a.phoneNumberCalled && (
                                <div className="text-muted md-typescale-body-small">
                                  Tel: <a href={`tel:${a.phoneNumberCalled}`}>{a.phoneNumberCalled}</a>
                                </div>
                              )}
                              {a.notes && (
                                <div className="text-muted md-typescale-body-small mt-1">
                                  Note: {a.notes}
                                </div>
                              )}
                              {a.callbackScheduledAt && (
                                <div className="text-muted md-typescale-body-small">
                                  Richiamo previsto: {fmtDateTime(a.callbackScheduledAt)}
                                </div>
                              )}
                              {a.callbackScheduledAt && a.callbackReason && (
                                <div className="md-typescale-body-small">
                                  <StatusPill tone="warning" className="me-1">Riapre il lead</StatusPill>
                                  Motivo del ricontatto: {a.callbackReason}
                                </div>
                              )}
                            </div>
                            <div className="text-end md-typescale-body-small text-nowrap ms-3">
                              <div className="text-muted">{fmtDateTime(a.occurredAt)}</div>
                            </div>
                          </div>
                        </li>
                      )
                    })}
                  </ul>
                )}
              </CardContent>
            </Card>

            {/* REQ-061-002 (spec section 61): "Percorso Lead" — the reconstructed path across status
                changes, appointments, conversion and (once converted) the resulting contract's own
                status history, merged into one chronological timeline by buildJourneyTimeline above.
                No monetary figure is ever shown here, matching the backend endpoint's own payload. */}
            <Card variant="outlined">
              <CardHeader title="Percorso lead" />
              <CardContent>
                {journeyError && (
                  <Callout tone="warning">{journeyError}</Callout>
                )}
                {!journeyError && journey && (() => {
                  const timeline = buildJourneyTimeline(journey)
                  return timeline.length === 0 ? (
                    <p className="text-muted mb-0">Nessun evento registrato finora.</p>
                  ) : (
                    <ul className="list-unstyled mb-0 d-flex flex-column gap-3">
                      {timeline.map((ev, i) => {
                        const tone = TONE_TOKENS[pillTone(ev.color)]
                        return (
                          <li key={i} className="d-flex gap-2">
                            <span className="d-flex align-items-center justify-content-center rounded-circle p-1 flex-shrink-0"
                              style={{ background: `hsl(var(--md-${tone}-container))`, color: `hsl(var(--md-on-${tone}-container))` }}>
                              <Icon name={ev.icon} size={18} />
                            </span>
                            <div>
                              <div className="md-typescale-emphasized-body-medium">{ev.label}</div>
                              <div className="text-muted md-typescale-body-small">{fmtDateTime(ev.at)}</div>
                              {ev.detail && <div className="md-typescale-body-small">{ev.detail}</div>}
                            </div>
                          </li>
                        )
                      })}
                    </ul>
                  )
                })()}
              </CardContent>
            </Card>
          </>
        }
        pane={
          <>
            {/* Right: sidebar */}
            {/* Status card */}
            <Card variant="outlined">
              <CardHeader title="Stato & pipeline" />
              <CardContent>
                <div className="d-flex flex-wrap gap-2">
                  {STATUS_BUTTONS.map(s => {
                    const isCurrent = lead.status === s.key
                    // TASK-0034: a button is only clickable if it's the current status (a no-op click,
                    // same as before) or a status the backend's LeadStatusTransitions graph actually
                    // allows from here — e.g. once a lead is 'lost' or 'converted' every button below
                    // is disabled, and from 'new' only 'contacted'/'lost' are, matching the backend's
                    // validated state machine instead of letting every status stay clickable
                    // regardless of the current one.
                    const isReachable = isCurrent || (lead.availableStatusTransitions ?? []).includes(s.key)
                    return (
                      <Chip
                        key={s.key}
                        variant="filter"
                        selected={isCurrent}
                        disabled={statusUpdating || !isReachable}
                        title={isReachable ? undefined : `Non disponibile da '${STATUS_LABELS[lead.status] ?? lead.status}'`}
                        onClick={() => s.key === 'lost' ? openLostModal() : void handleStatusChange(s.key)}
                      >
                        {s.label}
                      </Chip>
                    )
                  })}
                </div>
                {/* F-102 (REQ-068-001): stato operativo / esito finale configurabile, filtrato per lo stato corrente.
                    Gli esiti che chiudono il lead (Perso) sono offerti solo se il passaggio è ammesso dal ciclo di vita. */}
                {(() => {
                  const reachable = lead.availableStatusTransitions ?? []
                  const sameLifecycle = detailDefs.filter(d => d.lifecycleStatus === lead.status)
                  const outcomes = detailDefs.filter(
                    d => d.kind === 'esito_finale' && d.lifecycleStatus !== lead.status && d.lifecycleStatus !== 'converted' && reachable.includes(d.lifecycleStatus),
                  )
                  if (sameLifecycle.length === 0 && outcomes.length === 0) return null
                  const current = lead.statusDetailCode ?? ''
                  return (
                    <div className="mt-3">
                      <Select
                        id="lead-status-detail"
                        label="Stato operativo / esito"
                        value={current}
                        disabled={statusUpdating}
                        onChange={e => void handleDetailChange(e.target.value)}
                      >
                        <option value="">— nessuno —</option>
                        {current && !sameLifecycle.some(d => d.code === current) && (
                          <option value={current}>{current}</option>
                        )}
                        {sameLifecycle.length > 0 && (
                          <optgroup label="Per lo stato corrente">
                            {sameLifecycle.map(d => (
                              <option key={d.code} value={d.code}>
                                {d.label}{d.kind === 'esito_finale' ? ' (esito)' : ''}
                              </option>
                            ))}
                          </optgroup>
                        )}
                        {outcomes.length > 0 && (
                          <optgroup label="Chiudi il lead con un esito">
                            {outcomes.map(d => (
                              <option key={d.code} value={d.code}>
                                {d.label}{d.lostReason ? ` — ${d.lostReason}` : ''}
                              </option>
                            ))}
                          </optgroup>
                        )}
                      </Select>
                    </div>
                  )
                })()}
              </CardContent>
            </Card>

            {/* Quick actions card */}
            <Card variant="outlined">
              <CardHeader title="Azioni rapide" />
              <CardContent className="d-flex flex-column gap-2">
                {lead.status !== 'converted' && (
                  <Button
                    variant="filled"
                    size="sm"
                    icon="person_add"
                    onClick={openConvertModal}
                  >
                    Converti in cliente
                  </Button>
                )}
                {/* TASK-0050: only shown once the lead is actually converted — before that there is
                    no `convertedCustomerId` to pass along, same gating principle as "Converti in
                    Cliente" above (that one shows while NOT converted, this one while converted). */}
                {lead.status === 'converted' && (
                  <Button
                    variant="outlined"
                    size="sm"
                    icon="description"
                    onClick={() => navigate('/contracts/create?customerId=' + lead.convertedCustomerId)}
                  >
                    Crea contratto
                  </Button>
                )}
                {dealsOn && (
                  <Button
                    variant="outlined"
                    size="sm"
                    icon="handshake"
                    onClick={() => navigate(`/deals/new?leadId=${lead.id}` + (lead.convertedCustomerId ? `&customerId=${lead.convertedCustomerId}` : ''))}
                  >
                    Crea trattativa
                  </Button>
                )}
                {dealsOn && (
                  <Button variant="text" size="sm" icon="list" onClick={() => navigate(`/deals?leadId=${lead.id}`)}>
                    Trattative del lead
                  </Button>
                )}
                <Button
                  variant="outlined"
                  size="sm"
                  icon="add_task"
                  onClick={openTaskModal}
                >
                  Aggiungi task
                </Button>
                {/* TASK-0182 (REQ-106-001): only shown while the lead is not already contested —
                    there is no un-contest action, so once isContested is true this button has
                    nothing left to do (same gating idiom as "Converti in Cliente"/"Crea Contratto"
                    immediately above, one visible per lead state). */}
                {!lead.isContested && (
                  <Button
                    variant="tonal"
                    size="sm"
                    icon="report_problem"
                    onClick={openContestModal}
                  >
                    Contesta
                  </Button>
                )}
                {/* F-120: decisione sulla contestazione, solo Admin e solo finche' e' in attesa (e' definitiva). */}
                {isAdmin && lead.isContested && (lead.contestStatus == null || lead.contestStatus === 'Aperta') && (
                  <div className="d-flex flex-column gap-1 align-items-start">
                    {decisionError && <Callout tone="error" className="w-100">{decisionError}</Callout>}
                    <TextField
                      type="text"
                      label="Nota (facoltativa)"
                      placeholder=" "
                      maxLength={500}
                      value={decisionNote}
                      onChange={e => setDecisionNote(e.target.value)}
                      disabled={decisionSubmitting}
                    />
                    <div className="d-flex gap-1">
                      <Button
                        variant="outlined"
                        size="sm"
                        disabled={decisionSubmitting}
                        onClick={() => void handleDecideContest(true)}
                      >
                        Accetta
                      </Button>
                      <Button
                        variant="outlined"
                        size="sm"
                        disabled={decisionSubmitting}
                        onClick={() => void handleDecideContest(false)}
                      >
                        Respingi
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </>
        }
      />

      {/* TASK-0047: conversion modal — creates the Customer first (POST /api/customers, real
          since TASK-0046), then converts the lead with the resulting id. Same modal chrome as
          TargetsPage.tsx's create/edit modal; same DuplicateConflictError panel as
          CustomerFormPage.tsx's handleSave(forceCreate) flow, reused rather than reinvented. */}
      {showConvertModal && (
        <Dialog
          as="form"
          onSubmit={(e) => { e.preventDefault(); void handleCreateAndConvert(false) }}
          open
          onClose={closeConvertModal}
          title="Converti in cliente"
          supportingText={
            <>
              Crea un nuovo Cliente a partire dai dati di questo Lead, poi completa la
              conversione.
            </>
          }
          actions={
            <>
              <Button variant="text" onClick={closeConvertModal} disabled={converting}>
                Annulla
              </Button>
              {!duplicateConflict && (
                <Button
                  type="submit"
                  variant="text"
                  loading={converting}
                >
                  {converting ? 'Conversione...' : 'Crea Cliente e Converti'}
                </Button>
              )}
            </>
          }
        >
          {duplicateConflict ? (
            <Callout tone="warning" icon="report">
              <div className="d-flex flex-column gap-2">
                <div className="md-typescale-emphasized-body-medium">{duplicateConflict.message}</div>
                <ul className="list-unstyled ps-4 d-flex flex-column gap-1">
                  {duplicateConflict.candidates.map((c, i) => (
                    <li key={`${c.entityType}-${c.id}-${i}`}>
                      <StatusPill tone="neutral" className="me-2">{c.entityType}</StatusPill>
                      ID {c.id} — campo corrispondente: <strong>{c.matchedField}</strong>
                      {' '}
                      <a
                        href={candidateApiUrl(c)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="ms-2"
                      >
                        Vedi record esistente
                      </a>
                    </li>
                  ))}
                </ul>
                <div className="d-flex gap-2">
                  <Button
                    variant="tonal"
                    size="sm"
                    disabled={converting}
                    onClick={() => void handleCreateAndConvert(true)}
                  >
                    {converting ? 'Creazione...' : 'Crea comunque'}
                  </Button>
                  <Button
                    variant="text"
                    size="sm"
                    disabled={converting}
                    onClick={() => setDuplicateConflict(null)}
                  >
                    Annulla
                  </Button>
                </div>
              </div>
            </Callout>
          ) : (
            convertError && <Callout tone="error">{convertError}</Callout>
          )}

          {!duplicateConflict && (
            <FormGrid>
              <Select
                id="cv-customerType"
                label="Tipo Cliente"
                value={convertForm.customerType}
                onChange={e => setConvertForm(f => ({
                  ...f,
                  customerType: e.target.value as 'person' | 'company',
                }))}
              >
                <option value="person">Persona</option>
                <option value="company">Azienda</option>
              </Select>
              <TextField
                id="cv-companyName"
                type="text"
                label="Ragione Sociale / Azienda"
                placeholder=" "
                value={convertForm.companyName}
                onChange={e => setConvertForm(f => ({ ...f, companyName: e.target.value }))}
              />
              <TextField
                id="cv-firstName"
                type="text"
                label="Nome"
                placeholder=" "
                value={convertForm.firstName}
                onChange={e => setConvertForm(f => ({ ...f, firstName: e.target.value }))}
              />
              <TextField
                id="cv-lastName"
                type="text"
                label="Cognome"
                placeholder=" "
                value={convertForm.lastName}
                onChange={e => setConvertForm(f => ({ ...f, lastName: e.target.value }))}
              />
              <TextField
                id="cv-email"
                type="email"
                label="Email"
                placeholder=" "
                value={convertForm.email}
                onChange={e => setConvertForm(f => ({ ...f, email: e.target.value }))}
              />
              <TextField
                id="cv-phone"
                type="text"
                label="Telefono"
                placeholder=" "
                value={convertForm.phone}
                onChange={e => setConvertForm(f => ({ ...f, phone: e.target.value }))}
              />
            </FormGrid>
          )}
        </Dialog>
      )}

      {/* TASK-0050: "Aggiungi Task" modal — same chrome/fields as OperationsPage.tsx's TasksSection
          "New Task" modal, reused rather than reinvented. The only behavioral difference is that
          the create request always carries `leadId: lead.id` (see handleCreateTask above), and
          there is no status gate on the button that opens this — a task can be added to a lead in
          any state. */}
      {showTaskModal && (
        <Dialog
          as="form"
          onSubmit={(e) => { e.preventDefault(); void handleCreateTask() }}
          open
          onClose={closeTaskModal}
          title="Nuovo task"
          actions={
            <>
              <Button variant="text" onClick={closeTaskModal} disabled={savingTask}>
                Annulla
              </Button>
              <Button
                type="submit"
                variant="text"
                disabled={!taskForm.title.trim() || savingTask}
              >
                {savingTask ? 'Salvataggio...' : 'Salva'}
              </Button>
            </>
          }
        >
          <FormGrid>
            <TextField
              className="md-span-full"
              type="text"
              label="Titolo *"
              placeholder=" "
              required
              value={taskForm.title}
              onChange={e => setTaskForm(f => ({ ...f, title: e.target.value }))}
            />
            <Select
              label="Priorità"
              value={taskForm.priority}
              onChange={e => setTaskForm(f => ({ ...f, priority: e.target.value }))}
            >
              <option value="normal">Normale</option>
              <option value="low">Bassa</option>
              <option value="high">Alta</option>
              <option value="urgent">Urgente</option>
            </Select>
            <TextField
              type="date"
              label="Scadenza"
              value={taskForm.dueDate}
              onChange={e => setTaskForm(f => ({ ...f, dueDate: e.target.value }))}
            />
            <TextField
              className="md-span-full"
              label="Descrizione"
              placeholder=" "
              multiline
              rows={2}
              value={taskForm.description}
              onChange={e => setTaskForm(f => ({ ...f, description: e.target.value }))}
            />
          </FormGrid>
        </Dialog>
      )}

      {/* TASK-0150 (REQ-069-001): "Perso" reason modal — same chrome as the "Converti in Cliente"
          modal above, reused rather than reinvented (per the task card's instruction to use
          `openConvertModal` as the structural reference). Requires picking one of the 14 applied
          `LeadLostReasons` values before the transition is actually sent. */}
      {showLostModal && (
        <Dialog
          as="form"
          onSubmit={(e) => { e.preventDefault(); void handleConfirmLost() }}
          open
          onClose={closeLostModal}
          title="Segna come perso"
          supportingText="Seleziona il motivo per cui questo lead è stato perso."
          actions={
            <>
              <Button variant="text" onClick={closeLostModal} disabled={lostSubmitting}>
                Annulla
              </Button>
              <Button
                type="submit"
                variant="text"
                tone="error"
                disabled={!lostReason}
                loading={lostSubmitting}
              >
                {lostSubmitting ? 'Aggiornamento...' : 'Conferma'}
              </Button>
            </>
          }
        >
          {lostError && <Callout tone="error">{lostError}</Callout>}

          <FormGrid columns={1}>
            <Select
              id="lost-reason"
              label="Motivo *"
              value={lostReason}
              onChange={e => setLostReason(e.target.value)}
            >
              <option value="">Seleziona un motivo...</option>
              {LOST_REASONS.map(r => (
                <option key={r} value={r}>{r}</option>
              ))}
            </Select>
          </FormGrid>
        </Dialog>
      )}

      {/* TASK-0182 (REQ-106-001): "Contesta" reason modal — same chrome as the "Perso" reason modal
          above (openLostModal/closeLostModal/handleConfirmLost's modal), copied structurally and
          renamed for contest. Requires picking one of the 6 applied `LeadContestReasons` values
          before the contest is actually sent. */}
      {showContestModal && (
        <Dialog
          as="form"
          onSubmit={(e) => { e.preventDefault(); void handleConfirmContest() }}
          open
          onClose={closeContestModal}
          title="Contesta lead"
          supportingText="Seleziona il motivo per cui questo lead viene contestato."
          actions={
            <>
              <Button variant="text" onClick={closeContestModal} disabled={contestSubmitting}>
                Annulla
              </Button>
              <Button
                type="submit"
                variant="text"
                disabled={!contestReason}
                loading={contestSubmitting}
              >
                {contestSubmitting ? 'Invio...' : 'Conferma'}
              </Button>
            </>
          }
        >
          {contestError && <Callout tone="error">{contestError}</Callout>}

          <FormGrid columns={1}>
            <Select
              id="contest-reason"
              label="Motivo *"
              value={contestReason}
              onChange={e => setContestReason(e.target.value)}
            >
              <option value="">Seleziona un motivo...</option>
              {CONTEST_REASONS.map(r => (
                <option key={r} value={r}>{r}</option>
              ))}
            </Select>
          </FormGrid>
        </Dialog>
      )}

      {/* TASK-0278 (F-025): "Registra Chiamata" modal — capture outcome, phone, notes, and optional callback date. */}
      {showCallModal && (
        <Dialog
          as="form"
          onSubmit={(e) => { e.preventDefault(); void handleCreateCallAttempt() }}
          open
          onClose={closeCallModal}
          title="Registra chiamata"
          supportingText="Registra un nuovo tentativo di contatto su questo lead."
          actions={
            <>
              <Button variant="text" onClick={closeCallModal} disabled={savingCall}>
                Annulla
              </Button>
              <Button
                type="submit"
                variant="text"
                disabled={!callForm.outcomeId
                  || (!!callForm.callbackReason?.trim() && !callForm.callbackScheduledAt)}
                loading={savingCall}
              >
                {savingCall ? 'Salvataggio...' : 'Registra'}
              </Button>
            </>
          }
        >
          {callError && <Callout tone="error" role="alert">{callError}</Callout>}

          <FormGrid>
            <Select
              id="call-outcome"
              label="Esito *"
              value={callForm.outcomeId}
              onChange={e => setCallForm(f => ({ ...f, outcomeId: parseInt(e.target.value, 10) || 0 }))}
              disabled={savingCall}
            >
              <option value="0">Seleziona esito...</option>
              {outcomes.map(o => (
                <option key={o.id} value={o.id}>{o.label}</option>
              ))}
            </Select>

            <TextField
              id="call-phone"
              type="tel"
              label="Numero Telefonico"
              placeholder=" "
              value={callForm.phoneNumberCalled || ''}
              onChange={e => setCallForm(f => ({ ...f, phoneNumberCalled: e.target.value }))}
              disabled={savingCall}
            />
            <TextField
              className="md-span-full"
              id="call-callback"
              type="datetime-local"
              label="Data Richiamo Previsto"
              value={callForm.callbackScheduledAt || ''}
              min={callForm.callbackReason?.trim() ? localDateTimeMin() : undefined}
              supportingText="Massimo 24 mesi da oggi. Con il motivo, la data deve essere futura."
              onChange={e => setCallForm(f => ({ ...f, callbackScheduledAt: e.target.value }))}
              disabled={savingCall}
            />

            <div className="md-span-full">
              <TextField
                id="call-reason"
                type="text"
                label="Motivo del ricontatto"
                maxLength={RECONTACT_REASON_MAX}
                value={callForm.callbackReason || ''}
                onChange={e => setCallForm(f => ({ ...f, callbackReason: e.target.value }))}
                disabled={savingCall}
                placeholder="es. vincolato con altro fornitore fino a dicembre"
                supportingText={
                  <>
                    <strong>Con il motivo</strong> (e una data) un lead perso verrà <strong>riaperto
                    automaticamente</strong> a quella data, con un compito e una notifica.{' '}
                    <strong>Senza motivo</strong> è solo un promemoria nella coda di lavoro: il lead non viene riaperto.
                  </>
                }
              />
              {callForm.callbackReason?.trim() && !callForm.callbackScheduledAt && (
                <div className="text-danger md-typescale-body-small">Il motivo richiede una data di richiamo.</div>
              )}
            </div>
            <TextField className="md-span-full"
              id="call-notes"
              label="Note"
              placeholder=" "
              multiline
              rows={2}
              value={callForm.notes || ''}
              onChange={e => setCallForm(f => ({ ...f, notes: e.target.value }))}
              disabled={savingCall}
            />
          </FormGrid>
        </Dialog>
      )}
    </Page>
  )
}
